/*
Based on Material Dashboard 2 React by Creative Tim (https://www.creative-tim.com)
Licensed under the MIT License (see LICENSE)

Original Copyright (c) 2013-2021 Creative Tim
Modifications (c) 2025 CodeWeave Solutions (Pty) Ltd
*/

import { useEffect, useState, useMemo, useCallback } from "react";
import { getIndividualClients, getEntityClients, getIndividualClient, getEntityClient, postIndividualClient, postEntityClient, postBulkIndividualClients, postBulkEntityClients, updateIndividualClient, updateEntityClient } from "../../api/clients";
import { getTemplateFolders } from "../../api/contract_templates";
import { extractDOBFromId, isValidIdOrPassport } from "../../utils/extractDOBFromId";
import { generateIndividualClientTemplate, generateEntityClientTemplate, parseExcelFile, validateClientData, downloadExcelFile } from "../../utils/excelUtils";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ListItemIcon from "@mui/material/ListItemIcon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";



// Data
import clientsTableData from "layouts/clients/data/clientsTableData";
import Skeleton from "@mui/material/Skeleton";
import ClientDetailsModal from "components/ClientDetailsModal";

/**
 * TableSkeleton component for loading state.
 * 
 * @returns {JSX.Element} Skeleton loading component
 */
function TableSkeleton() {
  return (
    <MDBox>
      {[...Array(6)].map((_, i) => (
        <MDBox key={i} display="flex" alignItems="center" py={2} borderBottom="1px solid #eee">
          {/* client */}
          <MDBox display="flex" alignItems="center" width="35%">
            <Skeleton variant="circular" width={36} height={36} />
            <MDBox ml={2}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={120} height={16} />
            </MDBox>
          </MDBox>
          {/* info */}
          <MDBox width="25%" ml={2}>
            <Skeleton variant="text" width={80} height={18} />
            <Skeleton variant="text" width={100} height={14} />
          </MDBox>
          {/* status */}
          <MDBox width="15%" display="flex" justifyContent="center">
            <Skeleton variant="rounded" width={48} height={24} />
          </MDBox>
          {/* actions */}
          <MDBox width="25%" display="flex" justifyContent="center" gap={1}>
            <Skeleton variant="rectangular" width={60} height={32} />
            <Skeleton variant="rectangular" width={60} height={32} />
          </MDBox>
        </MDBox>
      ))}
    </MDBox>
  );
}

/**
 * Clients page for managing individual and entity clients.
 * Handles loading, error, and modal state. Uses best practices for React functional components.
 */
/**
 * Clients component for managing individual and entity clients.
 * 
 * @returns {JSX.Element} Clients management interface
 */
function Clients() {
  const [individualClients, setIndividualClients] = useState([]);
  const [entityClients, setEntityClients] = useState([]);
  const [loadingIndividuals, setLoadingIndividuals] = useState(true);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [entitiesFetched, setEntitiesFetched] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Template folders state
  const [templateFolders, setTemplateFolders] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [individualModalOpen, setIndividualModalOpen] = useState(false);
  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [individualFormData, setIndividualFormData] = useState({
    name: "",
    idNumber: "",
    email: "",
    phone: "",
    countryCode: "+27",
    address: "",
    taxNumber: "",
    dateOfBirth: "",
    dateOfBirthManuallyEdited: false
  });
  const [entityFormData, setEntityFormData] = useState({
    name: "",
    legalName: "",
    registrationNumber: "",
    licenseNumber: "",
    email: "",
    phone: "",
    countryCode: "+27",
    address: ""
  });
  const [individualErrors, setIndividualErrors] = useState({});
  const [entityErrors, setEntityErrors] = useState({});
  
  // Import functionality state
  const [individualModalTab, setIndividualModalTab] = useState(0);
  const [individualImportFile, setIndividualImportFile] = useState(null);
  const [individualImportData, setIndividualImportData] = useState([]);
  const [individualImportErrors, setIndividualImportErrors] = useState([]);
  const [entityModalTab, setEntityModalTab] = useState(0);
  const [entityImportFile, setEntityImportFile] = useState(null);
  const [entityImportData, setEntityImportData] = useState([]);
  const [entityImportErrors, setEntityImportErrors] = useState([]);
  
  // Client details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientType, setSelectedClientType] = useState(null);
  const [updatingClient, setUpdatingClient] = useState(false);

  // Fetch individual clients and template folders on mount
  useEffect(() => {
    async function fetchInitialData() {
      setLoadingIndividuals(true);
      setLoadingTemplates(true);
      setError(null);
      setTemplatesError(null);
      
      try {
        // Fetch individual clients and template folders in parallel
        const [individualsData, templatesData] = await Promise.all([
          getIndividualClients(),
          getTemplateFolders()
        ]);
        
        setIndividualClients(individualsData.map(c => ({ ...c, type: 'individual' })));
        setTemplateFolders(templatesData);
      } catch (err) {
        if (err.message.includes('template')) {
          setTemplatesError("Failed to fetch template folders");
          setTemplateFolders([]);
        } else {
          setError("Failed to fetch individual clients");
          setIndividualClients([]);
        }
      } finally {
        setLoadingIndividuals(false);
        setLoadingTemplates(false);
      }
    }
    fetchInitialData();
  }, []);

  // Fetch entity clients only when tab is clicked for the first time
  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1 && !entitiesFetched) {
      setLoadingEntities(true);
      setError(null);
      getEntityClients()
        .then(data => {
          setEntityClients(data.map(c => ({ ...c, type: 'entity' })));
          setEntitiesFetched(true);
        })
        .catch(() => {
          setError("Failed to fetch entity clients");
          setEntityClients([]);
        })
        .finally(() => setLoadingEntities(false));
    }
  }, [entitiesFetched]);

  // Handle fetching client data
  const handleFetchClient = useCallback(async (clientId, clientType) => {
    if (clientType === 'individual') {
      return await getIndividualClient(clientId);
    } else if (clientType === 'entity') {
      return await getEntityClient(clientId);
    }
    throw new Error('Invalid client type');
  }, []);

  // Handle client name click
  const handleClientClick = useCallback((client) => {
    setSelectedClient(client);
    setSelectedClientType(client.type || 'individual');
    setDetailsModalOpen(true);
  }, []);

  // Handle client update
  const handleClientUpdate = useCallback(async (formData) => {
    if (!selectedClient || !selectedClientType) return;

    setUpdatingClient(true);
    try {
      if (selectedClientType === 'individual') {
        await updateIndividualClient(selectedClient.id, formData);
        // Refresh individual clients list
        const updatedClients = await getIndividualClients();
        setIndividualClients(updatedClients.map(c => ({ ...c, type: 'individual' })));
      } else if (selectedClientType === 'entity') {
        await updateEntityClient(selectedClient.id, formData);
        // Refresh entity clients list
        const updatedClients = await getEntityClients();
        setEntityClients(updatedClients.map(c => ({ ...c, type: 'entity' })));
      }
      setDetailsModalOpen(false);
      setSelectedClient(null);
      setSelectedClientType(null);
    } catch (error) {
      console.error('Error updating client:', error);
      alert(`Failed to update client: ${error.message}`);
    } finally {
      setUpdatingClient(false);
    }
  }, [selectedClient, selectedClientType]);

  // Merge for table logic
  const allClients = [...individualClients, ...entityClients];
  const { columns, rows } = clientsTableData(allClients, templateFolders, handleClientClick);

  // Split clients into individual and entity with search and sorting
  const individualRows = useMemo(() => {
    return rows
      .filter(row => row.type === 'individual')
      .filter(row => {
        if (!searchQuery.trim()) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          (row.searchName || "").toLowerCase().includes(searchLower) ||
          (row.searchEmail || "").toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => {
        return (a.searchName || "").localeCompare(b.searchName || "");
      });
  }, [rows, searchQuery, loadingTemplates]);

  const entityRows = useMemo(() => {
    return rows
      .filter(row => row.type === 'entity')
      .filter(row => {
        if (!searchQuery.trim()) return true;
        const searchLower = searchQuery.toLowerCase();
        return (
          (row.searchName || "").toLowerCase().includes(searchLower) ||
          (row.searchEmail || "").toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => {
        return (a.searchName || "").localeCompare(b.searchName || "");
      });
  }, [rows, searchQuery, loadingTemplates]);

  const handleAddClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleIndividualClick = useCallback(() => {
    setAnchorEl(null);
    setIndividualModalOpen(true);
  }, []);

  const handleEntityClick = useCallback(() => {
    setAnchorEl(null);
    setEntityModalOpen(true);
  }, []);

  const handleIndividualModalClose = useCallback(() => {
    setIndividualModalOpen(false);
    setIndividualFormData({
      name: "",
      idNumber: "",
      email: "",
      phone: "",
      countryCode: "+27",
      address: "",
      taxNumber: "",
      dateOfBirth: "",
      dateOfBirthManuallyEdited: false
    });
    setIndividualErrors({});
    // Reset import state
    setIndividualModalTab(0);
    setIndividualImportFile(null);
    setIndividualImportData([]);
    setIndividualImportErrors([]);
  }, []);

  const handleEntityModalClose = useCallback(() => {
    setEntityModalOpen(false);
    setEntityFormData({
      name: "",
      legalName: "",
      registrationNumber: "",
      licenseNumber: "",
      email: "",
      phone: "",
      countryCode: "+27",
      address: ""
    });
    setEntityErrors({});
    // Reset import state
    setEntityModalTab(0);
    setEntityImportFile(null);
    setEntityImportData([]);
    setEntityImportErrors([]);
  }, []);

  /**
   * Combine country code and phone number.
   * 
   * @param {string} countryCode - Country code
   * @param {string} phone - Phone number
   * @returns {string} Combined phone number
   */
  const combinePhone = (countryCode, phone) => {
    let trimmed = phone;
    if (/^\d{10}$/.test(phone)) {
      trimmed = phone.slice(1);
    }
    return `${countryCode}${trimmed}`;
  };

  const validateIndividual = useCallback(() => {
    const errors = {};
    if (!individualFormData.name.trim()) errors.name = "Name is required";
    if (!individualFormData.email.trim()) errors.email = "Email is required";
    else {
      const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailRegex.test(individualFormData.email)) errors.email = "Invalid email address";
    }
    
    // Validate ID/Passport number
    if (!individualFormData.idNumber.trim()) {
      errors.idNumber = "ID/Passport number is required";
    } else if (!isValidIdOrPassport(individualFormData.idNumber)) {
      errors.idNumber = "Please enter a valid ID number or passport number";
    }
    
    // Validate date of birth
    if (!individualFormData.dateOfBirth) {
      errors.dateOfBirth = "Date of birth is required";
    } else {
      const dob = new Date(individualFormData.dateOfBirth);
      const now = new Date();
      if (dob > now) {
        errors.dateOfBirth = "Date of birth cannot be in the future";
      }
    }
    
    return errors;
  }, [individualFormData]);

  const validateEntity = useCallback(() => {
    const errors = {};
    if (!entityFormData.name.trim()) errors.name = "Name is required";
    if (!entityFormData.email.trim()) errors.email = "Email is required";
    else {
      const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailRegex.test(entityFormData.email)) errors.email = "Invalid email address";
    }
    return errors;
  }, [entityFormData]);

  const handleIndividualSubmit = useCallback(async () => {
    const errors = validateIndividual();
    setIndividualErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const payload = {
        name: individualFormData.name,
        id_number: individualFormData.idNumber,
        email: individualFormData.email,
        phone: combinePhone(individualFormData.countryCode, individualFormData.phone),
        address: individualFormData.address,
        tax_number: individualFormData.taxNumber,
        date_of_birth: individualFormData.dateOfBirth,
      };
      await postIndividualClient(payload);
      const data = await getIndividualClients();
      setIndividualClients(data.map(c => ({ ...c, type: 'individual' })));
      handleIndividualModalClose();
    } catch (err) {
      setError(err.message || "Failed to add individual client");
    }
  }, [validateIndividual, individualFormData, handleIndividualModalClose]);

  const handleEntitySubmit = useCallback(async () => {
    const errors = validateEntity();
    setEntityErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const payload = {
        name: entityFormData.name,
        legal_name: entityFormData.legalName,
        registration_number: entityFormData.registrationNumber,
        license_number: entityFormData.licenseNumber,
        email: entityFormData.email,
        phone: combinePhone(entityFormData.countryCode, entityFormData.phone),
        address: entityFormData.address,
      };
      await postEntityClient(payload);
      const data = await getEntityClients();
      setEntityClients(data.map(c => ({ ...c, type: 'entity' })));
      handleEntityModalClose();
    } catch (err) {
      setError(err.message || "Failed to add entity client");
    }
  }, [validateEntity, entityFormData, handleEntityModalClose]);

  const handleIndividualFormChange = useCallback((field) => (event) => {
    let value = event.target.value;
    let newFormData = { 
      ...individualFormData,
      [field]: value
    };
    
    // If the field is idNumber, auto-extract DOB but don't override if user has manually edited it
    if (field === "idNumber") {
      const extractedDOB = extractDOBFromId(value);
      if (extractedDOB && !individualFormData.dateOfBirthManuallyEdited) {
        newFormData.dateOfBirth = extractedDOB;
      }
    }
    
    setIndividualFormData(newFormData);
  }, [individualFormData]);

  const handleDateOfBirthChange = useCallback((event) => {
    setIndividualFormData({
      ...individualFormData,
      dateOfBirth: event.target.value,
      dateOfBirthManuallyEdited: true // Mark that user has manually edited the date
    });
  }, [individualFormData]);

  const handleEntityFormChange = useCallback((field) => (event) => {
    setEntityFormData({
      ...entityFormData,
      [field]: event.target.value
    });
  }, [entityFormData]);

  // Import functionality handlers
  const handleIndividualTemplateDownload = useCallback(() => {
    const template = generateIndividualClientTemplate();
    downloadExcelFile(template, 'individual_clients_template.xlsx');
  }, []);

  const handleEntityTemplateDownload = useCallback(() => {
    const template = generateEntityClientTemplate();
    downloadExcelFile(template, 'entity_clients_template.xlsx');
  }, []);

  const handleIndividualFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIndividualImportFile(file);
    setIndividualImportErrors([]);

    try {
      const data = await parseExcelFile(file, 'individual');
      const validation = validateClientData(data, 'individual');
      
      setIndividualImportData(validation.validClients);
      setIndividualImportErrors(validation.errors);
    } catch (error) {
      setIndividualImportErrors([{ row: 0, client: {}, errors: [error.message] }]);
    }
  }, []);

  const handleEntityFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setEntityImportFile(file);
    setEntityImportErrors([]);

    try {
      const data = await parseExcelFile(file, 'entity');
      const validation = validateClientData(data, 'entity');
      
      setEntityImportData(validation.validClients);
      setEntityImportErrors(validation.errors);
    } catch (error) {
      setEntityImportErrors([{ row: 0, client: {}, errors: [error.message] }]);
    }
  }, []);

  const handleIndividualBulkImport = useCallback(async () => {
    if (individualImportErrors.length > 0) {
      alert('Please fix validation errors before importing');
      return;
    }

    try {
      const clients = individualImportData.map(client => {
        const clientData = {
          name: client.name,
        };
        
        // Only include optional fields if they have values
        if (client['id/passportnumber'] && client['id/passportnumber'].trim() !== '') {
          clientData.id_number = client['id/passportnumber'].trim();
        }
        if (client.email && client.email.trim() !== '') {
          clientData.email = client.email.trim();
        }
        if (client.phone && client.phone.trim() !== '') {
          clientData.phone = client.phone.trim();
        }
        if (client.address && client.address.trim() !== '') {
          clientData.address = client.address.trim();
        }
        if (client['taxnumber'] && client['taxnumber'].trim() !== '') {
          clientData.tax_number = client['taxnumber'].trim();
        }
        if (client['dateofbirth'] && client['dateofbirth'].toString().trim() !== '') {
          clientData.date_of_birth = client['dateofbirth'].toString().trim();
        }
        
        return clientData;
      });

      const result = await postBulkIndividualClients(clients);
      
      // Reset import state
      setIndividualImportFile(null);
      setIndividualImportData([]);
      setIndividualImportErrors([]);
      setIndividualModalTab(0);
      setIndividualModalOpen(false);
      
      // Refresh data with a small delay to ensure data is committed
      await new Promise(resolve => setTimeout(resolve, 500));
      const newClients = await getIndividualClients();
      setIndividualClients(newClients.map(c => ({ ...c, type: 'individual' })));
      
      // Show detailed results
      if (result.success) {
        alert(`Successfully imported all ${result.successful} individual clients`);
      } else {
        const errorMsg = `Imported ${result.successful} of ${result.total} clients.\n${result.failed} failed:\n${result.errors.map(e => `- ${e.client}: ${e.error}`).join('\n')}`;
        alert(errorMsg);
      }
    } catch (error) {
      let errorMessage = 'Import failed';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      console.error('Import error:', error);
      alert(`Import failed: ${errorMessage}`);
    }
  }, [individualImportData, individualImportErrors]);

  const handleEntityBulkImport = useCallback(async () => {
    if (entityImportErrors.length > 0) {
      alert('Please fix validation errors before importing');
      return;
    }

    try {
      const clients = entityImportData.map(client => {
        const clientData = {
          name: client.name,
        };
        
        // Only include optional fields if they have values
        if (client['legalname'] && client['legalname'].trim() !== '') {
          clientData.legal_name = client['legalname'].trim();
        }
        if (client.email && client.email.trim() !== '') {
          clientData.email = client.email.trim();
        }
        if (client.phone && client.phone.trim() !== '') {
          clientData.phone = client.phone.trim();
        }
        if (client.address && client.address.trim() !== '') {
          clientData.address = client.address.trim();
        }
        if (client['registrationnumber'] && client['registrationnumber'].trim() !== '') {
          clientData.registration_number = client['registrationnumber'].trim();
        }
        if (client['licensenumber'] && client['licensenumber'].trim() !== '') {
          clientData.license_number = client['licensenumber'].trim();
        }
        
        return clientData;
      });

      await postBulkEntityClients(clients);
      
      // Reset import state
      setEntityImportFile(null);
      setEntityImportData([]);
      setEntityImportErrors([]);
      setEntityModalTab(0);
      setEntityModalOpen(false);
      
      // Refresh data
      const newClients = await getEntityClients();
      setEntityClients(newClients.map(c => ({ ...c, type: 'entity' })));
      
      alert(`Successfully imported ${clients.length} entity clients`);
    } catch (error) {
      let errorMessage = 'Import failed';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail);
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      console.error('Import error:', error);
      alert(`Import failed: ${errorMessage}`);
    }
  }, [entityImportData, entityImportErrors]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
          <Grid item xs={12} sm={12} md={12} lg={12} sx={{ flex: 1 }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Clients
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={handleAddClick}
                  endIcon={<Icon>arrow_drop_down</Icon>}
                >
                  Add Client
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Individual" />
                  <Tab label="Entity" />
                </Tabs>
                <MDBox mb={2} px={2}>
                  <MDInput
                    placeholder="Search clients..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    fullWidth
                  />
                  <MDTypography variant="caption" color="text" mt={1} display="block">
                    {activeTab === 0
                      ? `${individualRows.length} individual client${individualRows.length === 1 ? '' : 's'} found`
                      : `${entityRows.length} entity client${entityRows.length === 1 ? '' : 's'} found`}
                  </MDTypography>
                </MDBox>
                {activeTab === 0 && (
                  loadingIndividuals ? (
                    <TableSkeleton />
                  ) : (
                    <DataTable
                      table={{ columns, rows: individualRows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  )
                )}
                {activeTab === 1 && (
                  loadingEntities ? (
                    <TableSkeleton />
                  ) : (
                    <DataTable
                      table={{ columns, rows: entityRows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  )
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Add Client Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleIndividualClick}>
          <ListItemIcon>
            <Icon>person</Icon>
          </ListItemIcon>
          <MDTypography variant="button" fontWeight="regular">
            Add Individual
          </MDTypography>
        </MenuItem>
        <MenuItem onClick={handleEntityClick}>
          <ListItemIcon>
            <Icon>business</Icon>
          </ListItemIcon>
          <MDTypography variant="button" fontWeight="regular">
            Add Entity
          </MDTypography>
        </MenuItem>
      </Menu>

      {/* Individual Client Modal */}
      <Dialog
        open={individualModalOpen}
        onClose={handleIndividualModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6">Add Individual Client</MDTypography>
        </DialogTitle>
        <DialogContent>
          <Tabs value={individualModalTab} onChange={(e, v) => setIndividualModalTab(v)} sx={{ mb: 2 }}>
            <Tab label="Manual Entry" />
            <Tab label="Import from Excel" />
          </Tabs>
          
          {individualModalTab === 0 && (
            <MDBox py={2} display="flex" flexDirection="column" gap={2}>
              <MDBox display="flex" gap={2}>
                <MDInput
                  label="Name"
                  value={individualFormData.name}
                  onChange={handleIndividualFormChange('name')}
                  sx={{ flex: 1 }}
                  error={!!individualErrors.name}
                  helperText={individualErrors.name}
                />
                <MDInput
                  label="ID/Passport Number"
                  value={individualFormData.idNumber}
                  onChange={handleIndividualFormChange('idNumber')}
                  placeholder="YYMMDD####### or passport number"
                  sx={{ flex: 1 }}
                  error={!!individualErrors.idNumber}
                  helperText={individualErrors.idNumber || "Enter ID number or passport number"}
                />
              </MDBox>
              
              <MDBox display="flex" gap={2}>
                <MDInput
                  label="Email"
                  type="email"
                  value={individualFormData.email}
                  onChange={handleIndividualFormChange('email')}
                  placeholder="john.doe@example.com"
                  sx={{ flex: 1 }}
                  error={!!individualErrors.email}
                  helperText={individualErrors.email}
                />
                <MDInput
                  label="Tax Number"
                  value={individualFormData.taxNumber}
                  onChange={handleIndividualFormChange('taxNumber')}
                  placeholder="1234567890"
                  sx={{ flex: 1 }}
                />
              </MDBox>
              
              <MDBox display="flex" gap={1} alignItems="center">
                <MDInput
                  label="Country Code"
                  value={individualFormData.countryCode}
                  onChange={handleIndividualFormChange('countryCode')}
                  select
                  sx={{ width: 140 }}
                >
                  <option value="+27">+27 (ZA)</option>
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  {/* Add more as needed */}
                </MDInput>
                <MDInput
                  label="Phone"
                  value={individualFormData.phone}
                  onChange={handleIndividualFormChange('phone')}
                  placeholder="123456789"
                  sx={{ flex: 1 }}
                />
                <MDInput
                  label="Date of Birth"
                  type="date"
                  value={individualFormData.dateOfBirth}
                  onChange={handleDateOfBirthChange}
                  sx={{ width: 180 }}
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>
              
              <MDInput
                label="Address"
                value={individualFormData.address}
                onChange={handleIndividualFormChange('address')}
                placeholder="123 Main St, Anytown, USA"
                multiline
                rows={2}
                fullWidth
              />
            </MDBox>
          )}
          
          {individualModalTab === 1 && (
            <MDBox py={2}>
              {/* Header Section */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium" color="dark">
                    Import from Excel
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={0.5}>
                    Upload a CSV, TSV, or Excel file to import multiple clients at once
                  </MDTypography>
                </MDBox>
                <MDButton 
                  onClick={handleIndividualTemplateDownload} 
                  color="info" 
                  variant="gradient"
                  size="small"
                  startIcon={<Icon>download</Icon>}
                >
                  Download Template
                </MDButton>
              </MDBox>
              
              {/* File Upload Section */}
              <MDBox mb={3}>
                <MDBox 
                  p={3} 
                  border="1px dashed" 
                  borderColor="grey.300" 
                  borderRadius="lg"
                  textAlign="center"
                  bgColor="grey.50"
                  sx={{ 
                    '&:hover': { 
                      borderColor: 'info.main',
                      bgColor: 'info.light',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.tsv"
                    onChange={handleIndividualFileUpload}
                    style={{ display: 'none' }}
                    id="individual-file-input"
                  />
                  <label htmlFor="individual-file-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                    <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Icon fontSize="large" color="info">cloud_upload</Icon>
                      <MDTypography variant="h6" color="info" fontWeight="medium">
                        Choose File to Upload
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Supports Excel (.xlsx, .xls), CSV (.csv), and TSV (.tsv) files
                      </MDTypography>
                    </MDBox>
                  </label>
                </MDBox>
                
                {individualImportFile && (
                  <MDBox mt={2} p={2} bgColor="success.light" borderRadius="md" display="flex" alignItems="center" gap={1}>
                    <Icon color="success">check_circle</Icon>
                    <MDTypography variant="body2" color="success.dark">
                      Selected: {individualImportFile.name}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              
              {/* Validation Errors */}
              {individualImportErrors.length > 0 && (
                <MDBox mb={3}>
                  <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon color="error">error</Icon>
                    <MDTypography variant="h6" color="error" fontWeight="medium">
                      Validation Errors ({individualImportErrors.length})
                    </MDTypography>
                  </MDBox>
                  <MDBox maxHeight="200px" overflow="auto">
                    {individualImportErrors.map((error, index) => (
                      <MDBox key={index} mb={1} p={2} bgColor="error.light" borderRadius="md" border="1px solid" borderColor="error.main">
                        <MDTypography variant="body2" color="error.dark" fontWeight="medium">
                          Row {error.row}: {error.errors.join(', ')}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              )}
              
              {/* Data Preview */}
              {individualImportData.length > 0 && (
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon color="success">visibility</Icon>
                    <MDTypography variant="h6" color="success.dark" fontWeight="medium">
                      Preview ({individualImportData.length} valid clients)
                    </MDTypography>
                  </MDBox>
                  <MDBox 
                    maxHeight="300px" 
                    overflow="auto" 
                    border="1px solid" 
                    borderColor="grey.300" 
                    borderRadius="md"
                    bgColor="white"
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Name</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Email</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Phone</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>ID/Passport</th>
                        </tr>
                      </thead>
                      <tbody>
                        {individualImportData.slice(0, 10).map((client, index) => (
                          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.name}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.email}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.phone}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client['id/passportnumber']}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {individualImportData.length > 10 && (
                      <MDBox p={2} textAlign="center" bgColor="grey.100">
                        <MDTypography variant="caption" color="text" fontWeight="medium">
                          ... and {individualImportData.length - 10} more clients
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleIndividualModalClose} color="dark" variant="outlined">
            Cancel
          </MDButton>
          {individualModalTab === 0 ? (
            <MDButton onClick={handleIndividualSubmit} color="info" variant="gradient">
              Add Client
            </MDButton>
          ) : (
            <MDButton 
              onClick={handleIndividualBulkImport} 
              color="info" 
              variant="gradient"
              disabled={individualImportData.length === 0 || individualImportErrors.length > 0}
            >
              Import {individualImportData.length} Clients
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Entity Client Modal */}
      <Dialog
        open={entityModalOpen}
        onClose={handleEntityModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6">Add Entity Client</MDTypography>
        </DialogTitle>
        <DialogContent>
          <Tabs value={entityModalTab} onChange={(e, v) => setEntityModalTab(v)} sx={{ mb: 2 }}>
            <Tab label="Manual Entry" />
            <Tab label="Import from Excel" />
          </Tabs>
          
          {entityModalTab === 0 && (
            <MDBox py={2} display="flex" flexDirection="column" gap={2}>
              <MDBox display="flex" gap={2}>
                <MDInput
                  label="Name"
                  value={entityFormData.name}
                  onChange={handleEntityFormChange('name')}
                  placeholder="Fisher Dugmore Financial"
                  sx={{ flex: 1 }}
                  error={!!entityErrors.name}
                  helperText={entityErrors.name}
                />
                <MDInput
                  label="Organization Legal Name"
                  value={entityFormData.legalName}
                  onChange={handleEntityFormChange('legalName')}
                  placeholder="Fisher Dugmore Centurion (Pty) Ltd"
                  sx={{ flex: 1 }}
                />
              </MDBox>
              
              <MDBox display="flex" gap={2}>
                <MDInput
                  label="Email"
                  type="email"
                  value={entityFormData.email}
                  onChange={handleEntityFormChange('email')}
                  placeholder="entity@example.com"
                  sx={{ flex: 1 }}
                  error={!!entityErrors.email}
                  helperText={entityErrors.email}
                />
                <MDInput
                  label="Entity Registration Number"
                  value={entityFormData.registrationNumber}
                  onChange={handleEntityFormChange('registrationNumber')}
                  placeholder="1234567890"
                  sx={{ flex: 1 }}
                />
              </MDBox>
              
              <MDBox display="flex" gap={1} alignItems="center">
                <MDInput
                  label="Country Code"
                  value={entityFormData.countryCode}
                  onChange={handleEntityFormChange('countryCode')}
                  select
                  sx={{ width: 140 }}
                >
                  <option value="+27">+27 (ZA)</option>
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  {/* Add more as needed */}
                </MDInput>
                <MDInput
                  label="Phone"
                  value={entityFormData.phone}
                  onChange={handleEntityFormChange('phone')}
                  placeholder="123456789"
                  sx={{ flex: 1 }}
                />
                <MDInput
                  label="License Number"
                  value={entityFormData.licenseNumber}
                  onChange={handleEntityFormChange('licenseNumber')}
                  placeholder="1234567890"
                  sx={{ flex: 1 }}
                />
              </MDBox>
              
              <MDInput
                label="Address"
                value={entityFormData.address}
                onChange={handleEntityFormChange('address')}
                placeholder="123 Main St, Anytown, USA"
                multiline
                rows={2}
                fullWidth
              />
            </MDBox>
          )}
          
          {entityModalTab === 1 && (
            <MDBox py={2}>
              {/* Header Section */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDBox>
                  <MDTypography variant="h5" fontWeight="medium" color="dark">
                    Import from Excel
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={0.5}>
                    Upload a CSV, TSV, or Excel file to import multiple entity clients at once
                  </MDTypography>
                </MDBox>
                <MDButton 
                  onClick={handleEntityTemplateDownload} 
                  color="info" 
                  variant="gradient"
                  size="small"
                  startIcon={<Icon>download</Icon>}
                >
                  Download Template
                </MDButton>
              </MDBox>
              
              {/* File Upload Section */}
              <MDBox mb={3}>
                <MDBox 
                  p={3} 
                  border="1px dashed" 
                  borderColor="grey.300" 
                  borderRadius="lg"
                  textAlign="center"
                  bgColor="grey.50"
                  sx={{ 
                    '&:hover': { 
                      borderColor: 'info.main',
                      bgColor: 'info.light',
                      cursor: 'pointer'
                    }
                  }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv,.tsv"
                    onChange={handleEntityFileUpload}
                    style={{ display: 'none' }}
                    id="entity-file-input"
                  />
                  <label htmlFor="entity-file-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                    <MDBox display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <Icon fontSize="large" color="info">cloud_upload</Icon>
                      <MDTypography variant="h6" color="info" fontWeight="medium">
                        Choose File to Upload
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Supports Excel (.xlsx, .xls), CSV (.csv), and TSV (.tsv) files
                      </MDTypography>
                    </MDBox>
                  </label>
                </MDBox>
                
                {entityImportFile && (
                  <MDBox mt={2} p={2} bgColor="success.light" borderRadius="md" display="flex" alignItems="center" gap={1}>
                    <Icon color="success">check_circle</Icon>
                    <MDTypography variant="body2" color="success.dark">
                      Selected: {entityImportFile.name}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              
              {/* Validation Errors */}
              {entityImportErrors.length > 0 && (
                <MDBox mb={3}>
                  <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon color="error">error</Icon>
                    <MDTypography variant="h6" color="error" fontWeight="medium">
                      Validation Errors ({entityImportErrors.length})
                    </MDTypography>
                  </MDBox>
                  <MDBox maxHeight="200px" overflow="auto">
                    {entityImportErrors.map((error, index) => (
                      <MDBox key={index} mb={1} p={2} bgColor="error.light" borderRadius="md" border="1px solid" borderColor="error.main">
                        <MDTypography variant="body2" color="error.dark" fontWeight="medium">
                          Row {error.row}: {error.errors.join(', ')}
                        </MDTypography>
                      </MDBox>
                    ))}
                  </MDBox>
                </MDBox>
              )}
              
              {/* Data Preview */}
              {entityImportData.length > 0 && (
                <MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                    <Icon color="success">visibility</Icon>
                    <MDTypography variant="h6" color="success.dark" fontWeight="medium">
                      Preview ({entityImportData.length} valid clients)
                    </MDTypography>
                  </MDBox>
                  <MDBox 
                    maxHeight="300px" 
                    overflow="auto" 
                    border="1px solid" 
                    borderColor="grey.300" 
                    borderRadius="md"
                    bgColor="white"
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Name</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Email</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Phone</th>
                          <th style={{ padding: '12px 16px', border: '1px solid #e9ecef', textAlign: 'left', fontWeight: 600, color: '#495057' }}>Registration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entityImportData.slice(0, 10).map((client, index) => (
                          <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.name}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.email}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client.phone}</td>
                            <td style={{ padding: '12px 16px', border: '1px solid #e9ecef', color: '#495057' }}>{client['registrationnumber']}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {entityImportData.length > 10 && (
                      <MDBox p={2} textAlign="center" bgColor="grey.100">
                        <MDTypography variant="caption" color="text" fontWeight="medium">
                          ... and {entityImportData.length - 10} more clients
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleEntityModalClose} color="dark" variant="outlined">
            Cancel
          </MDButton>
          {entityModalTab === 0 ? (
            <MDButton onClick={handleEntitySubmit} color="info" variant="gradient">
              Add Client
            </MDButton>
          ) : (
            <MDButton 
              onClick={handleEntityBulkImport} 
              color="info" 
              variant="gradient"
              disabled={entityImportData.length === 0 || entityImportErrors.length > 0}
            >
              Import {entityImportData.length} Clients
            </MDButton>
          )}
        </DialogActions>
      </Dialog>

      {/* Client Details Modal */}
      <ClientDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedClient(null);
          setSelectedClientType(null);
        }}
        client={selectedClient}
        clientType={selectedClientType || 'individual'}
        onSave={handleClientUpdate}
        loading={updatingClient}
        onFetchClient={handleFetchClient}
      />
    </DashboardLayout>
  );
}

export default Clients;
